import { useEffect, useState } from "react";
import { TextInput } from "@narmi/design_system";
import Filters from "byzantine/src/filters";
import { modules } from "byzantine";
import { AlertConfigurableField } from "byzantine/src/dbbl/businessLogic/modules/alerts";
import { formAdapters } from "../../../../../src";

interface AmountQueryFieldProps {
  option: AlertConfigurableField;
  alertKey: API.AlertKey;
  fieldName: "transactionQuery" | "thresholdAmount";
}

const AmountQueryField = ({
  option,
  alertKey,
  fieldName,
}: AmountQueryFieldProps) => {
  const { form } = modules.alerts.useUserAlertsForm(alertKey);
  const { value, onChange, ...transactionProps } =
    formAdapters.fieldWithOnChange(form, fieldName as any);

  // Convert the stored cent value to dollars and format it.
  const formatDisplayValue = (cents: string | number): string => {
    // Convert cents to dollars.
    const dollars = Filters.penniesToDollars(Number(cents));
    // Format dollars to include a "$", commas, and exactly two decimals.
    return Filters.currency(dollars, { hasDecimal: true });
  };

  // Local state for the displayed dollar amount.
  const [inputValue, setInputValue] = useState<string>(() =>
    formatDisplayValue(value)
  );

  // Whenever the form's cent value changes, update the display.
  useEffect(() => {
    setInputValue(formatDisplayValue(value));
  }, [value]);

  // Allow the user to type freely.
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  // When the field loses focus, normalize, parse, convert to cents, update form state, and reformat.
  const handleBlur = () => {
    // Remove "$" and commas.
    const normalized = inputValue.replace(/[$,]/g, "");
    const parsed = parseFloat(normalized);
    if (!Number.isNaN(parsed)) {
      // Update form state with cents as a number.
      onChange(Filters.dollarsToPennies(parsed as Dollars));
      // Reformat display value.
      setInputValue(Filters.currency(parsed as Dollars, { hasDecimal: true }));
    } else {
      // In case of invalid input, clear the field.
      onChange("");
      setInputValue("");
    }
  };

  return (
    <div>
      <div>{option.fieldSectionTitle}</div>
      <TextInput
        key={option.fieldKey}
        label={option.fieldLabel}
        value={inputValue}
        onChange={handleChange}
        {...transactionProps}
        onBlur={handleBlur}
      />
    </div>
  );
};

export default AmountQueryField;
